import { AutocompleteGetTagProps, useAutocomplete } from '@mui/base/useAutocomplete';
import CloseIcon from '@mui/icons-material/Close';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { SelectOption } from '../../types';

const Root = styled('div')(
  ({ theme }) => `
  color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'
    };
  font-size: 14px;
`,
);

const Label = styled('label')`
  padding: 0 0 4px;
  line-height: 1.5;
  display: block;
`;

interface InputWrapperProps {
  fullWidth?: boolean
  error?: boolean
}

const InputWrapper = styled('div')<InputWrapperProps>(
  ({ theme, fullWidth, error }) => `
  width: ${fullWidth ? '100%' : '300px'};
  border: 1px solid ${error ? '#d32f2f' : theme.palette.mode === 'dark' ? '#434343' : '#d9d9d9'};
  background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
  border-radius: 6px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &:hover {
    border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
  }

  &.focused {
    border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  & input {
    background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
    color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'
    };
    height: 37px;
    box-sizing: border-box;
    padding: 4px 6px;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
  }
`,
);

interface TagProps extends ReturnType<AutocompleteGetTagProps> {
  label: string;
}

function Tag(props: TagProps) {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      <CloseIcon onClick={onDelete} />
    </div>
  );
}

const StyledTag = styled(Tag)<TagProps>(
  ({ theme }) => `
  display: flex;
  align-items: center;
  height: 24px;
  margin: 2px;
  line-height: 22px;
  background-color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#fafafa'
    };
  border: 1px solid ${theme.palette.mode === 'dark' ? '#303030' : '#e8e8e8'};
  border-radius: 2px;
  box-sizing: content-box;
  padding: 0 4px 0 10px;
  outline: 0;
  overflow: hidden;

  &:focus {
    border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
    background-color: ${theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff'};
  }

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 12px;
    cursor: pointer;
    padding: 4px;
  }
`,
);

interface ListboxProps {
  fullWidth?: boolean
}

const Listbox = styled('ul')<ListboxProps>(
  ({ theme, fullWidth }) => `
  width: ${fullWidth ? 'calc(100% - 40px)' : '300px'};
  margin: 2px 0 0;
  padding: 0;
  position: absolute;
  list-style: none;
  background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
  overflow: auto;
  max-height: 250px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;

  & li {
    padding: 5px 12px;
    display: flex;

    & span {
      flex-grow: 1;
    }

    & svg {
      color: transparent;
    }
  }

  & li[aria-selected='true'] {
    background-color: ${theme.palette.mode === 'dark' ? '#2b2b2b' : '#fafafa'};
    font-weight: 600;

    & svg {
      color: #1890ff;
    }
  }

  & li.${autocompleteClasses.focused} {
    background-color: ${theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff'};
    cursor: pointer;

    & svg {
      color: currentColor;
    }
  }
`,
);

interface IProps {
  label?: string;
  options: SelectOption[];
  value?: SelectOption[];
  defaultValue?: SelectOption[];
  fullWidth?: boolean;
  onValueChange: (value: SelectOption[]) => void;
  helperText?: string;
  error?: boolean;
  placeholder?: string;
}

export default function MultiSelect({
  label,
  options,
  fullWidth = true,
  value,
  defaultValue = [],
  onValueChange,
  helperText,
  error = false,
  placeholder
}: IProps) {
  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value: internalValue,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    id: 'customized-hook-demo',
    value: value ?? undefined,
    defaultValue: value ? undefined : defaultValue,
    multiple: true,
    options,
    getOptionLabel: (option) => option.label,
    onChange: (_, newValue) => {
      onValueChange(newValue as SelectOption[]);
    },
  });

  return (
    <Root>
      <div {...getRootProps()}>
        {label && <Label {...getInputLabelProps()}>{label}</Label>}
        <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''} fullWidth={fullWidth} error={error}>
          {(internalValue as SelectOption[])?.map((option: SelectOption, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <StyledTag key={key} {...tagProps} label={option.label} />;
          })}
          <input
            {...getInputProps()}
            placeholder={internalValue.length === 0 ? placeholder ?? "Type to search..." : ""}
          />
        </InputWrapper>
      </div>
      {helperText && <p className='helper-text'>{helperText}</p>}
      {groupedOptions.length > 0 ? (
        <Listbox {...getListboxProps()} fullWidth={fullWidth}>
          {(groupedOptions as typeof options).map((option, index) => {
            const { key, ...optionProps } = getOptionProps({ option, index });
            return (
              <li key={key} {...optionProps}>
                <span>{option.label}</span>
                {/* <CheckIcon fontSize="small" /> */}
              </li>
            );
          })}
        </Listbox>
      ) : null}
    </Root>
  );
}